/**
 * Motor "escolher" — selecionar 1 entre 2-3 opcoes.
 *
 * D8 (inviolavel): no maximo 3 opcoes simultaneas. O schema do motor trava em 3.
 * D6: escolher a opcao que nao resolve o problema NAO e erro. O jogo mostra
 *     gentilmente o que aconteceu e devolve a escolha. Sem som de erro, sem
 *     tentativa perdida.
 */

import * as THREE from 'three';
import { BaseEngine } from './BaseEngine.js';

const CORES = [0xffb74d, 0x4fc3f7, 0xaed581];

export class EscolherEngine extends BaseEngine {
  constructor(ctx) {
    super(ctx);
    this.opcoes = [];
    this.grupo = null;
    this.escolhida = null;
  }

  async iniciar(tarefa, banda) {
    await super.iniciar(tarefa, banda);

    const payload = tarefa.bandas[String(banda)];
    if (!payload) {
      throw new Error(`FAZENDA: a tarefa "${tarefa.id}" nao tem payload para a banda ${banda}.`);
    }

    if (payload.opcoes > 3) {
      throw new Error(
        `FAZENDA: a tarefa "${tarefa.id}" pede ${payload.opcoes} opcoes na banda ${banda}. ` +
          'D8 permite no maximo 3 opcoes simultaneas.'
      );
    }

    this.quantidade = payload.opcoes;
    this.dicaVisual = payload.dica_visual === true;

    const opcoes = tarefa.conteudo?.opcoes;
    if (!Array.isArray(opcoes) || opcoes.length < this.quantidade) {
      throw new Error(
        `FAZENDA: a tarefa "${tarefa.id}" precisa de "conteudo.opcoes" com pelo menos ` +
          `${this.quantidade} opcoes. Cada uma com { "nome": ..., "certa": true|false }.`
      );
    }
    this.dados = embaralhar([...opcoes]).slice(0, this.quantidade);

    if (!this.dados.some((o) => o.certa)) {
      throw new Error(
        `FAZENDA: nenhuma opcao sorteada da tarefa "${tarefa.id}" tem "certa": true. ` +
          'Pelo menos uma opcao precisa resolver o problema.'
      );
    }

    this.montar();
    console.info(
      `FAZENDA: tarefa "${tarefa.id}" iniciada — banda ${banda}, ${this.quantidade} opcoes.`
    );
  }

  montar() {
    const { fazenda, sceneManager, input } = this.ctx;

    this.grupo = new THREE.Group();
    this.grupo.name = 'tarefa-escolher';
    const base = fazenda.posicaoDaAncora(this.tarefa.ancora);
    this.alturaBase = base.y + 1.0;
    const largura = (this.quantidade - 1) * 2.6;

    this.dados.forEach((dado, i) => {
      // Alvo bem grande: e uma escolha, tem que ser facil de acertar (D3).
      const opcao = new THREE.Mesh(
        new THREE.BoxGeometry(1.7, 1.7, 1.7),
        new THREE.MeshStandardMaterial({ color: CORES[i], roughness: 0.9, flatShading: true })
      );
      opcao.position.set(base.x - largura / 2 + i * 2.6, base.y + 1.0, base.z + 2.4);
      opcao.userData.dado = dado;

      this.grupo.add(opcao);
      this.opcoes.push(opcao);
      input.registrar(opcao, () => this.aoEscolher(opcao));
    });

    sceneManager.adicionar(this.grupo);
    input.conferirTamanhoDeAlvo(this.opcoes[0], `opcao da tarefa ${this.tarefa.id}`);
  }

  aoEscolher(opcao) {
    const { audio, instrucao, input } = this.ctx;
    this.tentativas += 1;

    if (opcao.userData.dado.certa) {
      this.escolhida = opcao.userData.dado;
      audio.tocarSucesso();
      for (const o of this.opcoes) input.desregistrar(o);
      return;
    }

    // D6 — nao existe "errado". A opcao recua e a escolha continua aberta.
    audio.tocarTentarDeNovo();
    opcao.userData.recuando = 1;
    instrucao.mostrarTexto('Hmm, essa nao resolveu. Tenta outra!');
  }

  concluiu() {
    return this.escolhida !== null;
  }

  atualizar(dt) {
    for (const opcao of this.opcoes) {
      // Dica visual: a opcao que resolve balanca de leve, chamando o olhar.
      if (this.dicaVisual && opcao.userData.dado.certa && !this.escolhida) {
        opcao.position.y = this.alturaBase + Math.sin(performance.now() / 350) * 0.12;
      }
      if (opcao.userData.recuando > 0) {
        opcao.userData.recuando = Math.max(0, opcao.userData.recuando - dt * 2);
        opcao.rotation.y = opcao.userData.recuando * 0.6;
      }
    }
  }

  async concluir() {
    const evento = await super.concluir({ concluiu: this.concluiu() });
    return { ...evento, escolha: this.escolhida?.nome ?? null };
  }

  descartar() {
    const { input, sceneManager } = this.ctx;
    for (const o of this.opcoes) input.desregistrar(o);
    if (this.grupo) {
      sceneManager.remover(this.grupo);
      this.grupo.traverse((o) => { o.geometry?.dispose(); o.material?.dispose(); });
    }
    this.opcoes = [];
    this.grupo = null;
    this.escolhida = null;
    super.descartar();
  }
}

function embaralhar(lista) {
  for (let i = lista.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [lista[i], lista[j]] = [lista[j], lista[i]];
  }
  return lista;
}
